<template>
  <div class="tutorial-root"  tabindex="0">
   <!-- Outro modal -->
<div v-if="showOutro" class="intro-backdrop" role="dialog" aria-modal="true">
  <div class="intro-modal">
    <h2>That’s it! 🎉</h2>
    <p>
      You’ve completed the tutorial. You’re ready to use the real Analysis Tool
      on your own.
    </p>
    <p class="readmore">
<router-link
  to="/toolguide"
  data-track="tut_outro_read_more"
>Read more →</router-link>
</p>

    <div class="intro-actions">
      <button
  class="btn ghost"
  @click="restart"
  data-track="tut_restart_from_outro"
>Restart Tutorial</button>
      <!-- Replace /analysis-tool with your actual route or URL -->

 <router-link
  to="/AnalysisTool"
  class="btn primary"
  data-tip="Analysis Tool"
  aria-label="Analysis Tool"
  data-track="tut_go_to_analysis_from_outro"
>Go to Analysis Tool</router-link>
    </div>
  </div>
</div>

   
    <!-- Intro modal -->
    <div v-if="showIntro" class="intro-backdrop" role="dialog" aria-modal="true">
      <div class="intro-modal" ref="introModal">
        <h2>Welcome to the Analysis Tool Tutorial</h2>
        <p>
          This is a guided, step‑by‑step walkthrough using a simplified version of the tool.
          It won’t submit real data.  It simply shows you exactly where to click and what to do.
        </p>
        <ul class="intro-points">
          <li>Copy an outgoing hash from the example transaction table</li>
          <li>Paste it into the tool</li>
          <li>Submit, wait briefly, then view the results</li>
        </ul>
        <div class="intro-actions">
        <button
  class="btn ghost"
  @click="skipTutorial"
  data-track="tut_skip_intro"
>Skip</button>
          <button
  class="btn primary"
  @click="beginTutorial"
  data-track="tut_begin_intro"
>I’m ready - begin</button>
        </div>
      </div>
    </div>

    <!-- Header -->
    <header class="tutorial-header" aria-hidden="true">
      <h1>How to Use the Analysis Tool (Hands-on Tutorial)</h1>
      <p class="sub">We’ll guide you step-by-step. Nothing here is “live” - it’s all for learning.</p>
    </header>

    <!-- Main content -->
    <section class="grid" aria-hidden="true">
      <!-- Mock Transaction History (Step 1 target) -->
      <article class="card">
        <h2 class="card-title">Example Transaction History</h2>
        <table class="mock-table">
          <thead>
            <tr><th>Date</th><th>Type</th><th>Hash</th></tr>
          </thead>
          <tbody>
            <tr>
              <td>2025-08-20</td><td>Incoming</td><td>0xabc123…</td>
            </tr>
        <tr class="highlighted"
    ref="txRow"
    :data-focus="!showIntro && step === 1 ? 'true' : null"
    data-focus-tag="Copy this outgoing hash"
    data-step="1"
    data-track="tut_click_outgoing_hash"
    :data-track-props="JSON.stringify({ step })">
              <td>2025-08-21</td>
              <td>Outgoing</td>
              <td class="brace-focus"><span><strong>0xdef45689a</strong></span></td>
            </tr>
            <tr>
              <td>2025-08-22</td><td>Incoming</td><td>0xt4r1213ac4er</td>
            </tr>
          </tbody>
        </table>
      </article>

      <!-- Mock Analysis Tool (Step 2 + 3 targets) -->
      <article class="card">
        <h2 class="card-title">Analysis Tool</h2>
        <div class="field">
          <label for="hashInput">Transaction Hash</label>
          <input
            id="hashInput"
            ref="hashInput"
            type="text"
            placeholder="Paste hash here"
            :data-focus="!showIntro && step === 2 ? 'true' : null"
            data-focus-tag="Paste here"
            data-step="2"
            v-model="fakeHash"
            @focus="ensureSpotlight()"
  data-track="tut_focus_hash_input"
  :data-track-props="JSON.stringify({ step })"
          />
          <p class="hint">Tip: You can paste <code>0xdef456…89a</code> from the example above.</p>
        </div>

        <div class="actions">
          <button
            class="btn primary"
            ref="submitBtn"
            :data-focus="!showIntro && step === 3 ? 'true' : null"
            data-focus-tag="Click Submit"
            data-step="3"
            @click="goToWaiting"
  data-track="tut_submit_click"
  :data-track-props="JSON.stringify({ step })"
          >
            Submit
          </button>
        </div>
      </article>

      <!-- Mock Results (Step 5+6 targets) -->
      <article class="card">
        <h2 class="card-title">Results</h2>

        <div v-if="step < 5" class="placeholder">
          <div class="skeleton"></div>
          <div class="skeleton wide"></div>
          <div class="skeleton"></div>
        </div>

        <div v-else class="results">
          <div class="result-row">
            <span class="pill pill--ok">Ready</span>
            <span>Example Analysis for <code>0xdef456…89a</code></span>
          </div>

          <div class="result-actions">
      <button
  class="btn"
  ref="viewReportBtn"
  :data-focus="!showIntro && step === 5 ? 'true' : null"
  data-focus-tag="View the detailed report"
  data-step="5"
  @click="openReport()"
  data-track="tut_view_report_click"
  :data-track-props="JSON.stringify({ step })"
>
  View Report
</button>

       <button
  class="btn"
  ref="viewImageBtn"
  :data-focus="!showIntro && step === 6 ? 'true' : null"
  data-focus-tag="Now view the image"
  data-step="6"
  @click="openImage()"
  data-track="tut_view_image_click"
  :data-track-props="JSON.stringify({ step })"
>
  View Image
</button>
          </div>
        </div>
      </article>
    </section>

    <!-- Bottom bar controls -->
    <footer class="controls" aria-hidden="true">
      <div class="left">
        <span class="step-chip">Step {{ step }} / 7</span>
      </div>
      <div class="right">
     <button
  class="btn ghost"
  @click="prevStep"
  :disabled="step===1"
  data-track="tut_prev_step"
  :data-track-props="JSON.stringify({ step_from: step })"
>Back</button>
      <button
  class="btn"
  v-if="step < 4"
  @click="nextStep"
  data-track="tut_next_step"
  :data-track-props="JSON.stringify({ step_from: step })"
>Next</button>
        <button class="btn" v-else-if="step === 4" disabled>
          Analyzing…
          <span class="dots"><span>.</span><span>.</span><span>.</span></span>
        </button>
       <button
  class="btn primary"
  v-else-if="step === 5"
  @click="nextStep"
  data-track="tut_next_after_report"
  :data-track-props="JSON.stringify({ step_from: step })"
>Next</button>

<button
  class="btn primary"
  v-else-if="step === 6"
  @click="nextStep"
  data-track="tut_next_after_image"
  :data-track-props="JSON.stringify({ step_from: step })"
>Next</button>

<button
  class="btn primary"
  v-else-if="step === 7"
  @click="restart"
  data-track="tut_restart_from_footer"
>Restart Tutorial</button>
      </div>
    </footer>

    <!-- Spotlight: fixed-position hole (hidden on step 4) -->
    <div
      v-show="!showIntro && step <= 7 && step !== 4 && hole.visible"
      class="spotlight-hole"
      :style="holeStyle"
      aria-hidden="true"
    ></div>

    <!-- Floating callout for focused steps (hidden on step 4) -->
    <div
      class="callout fixed"
      v-show="!showIntro && step <= 7 && step !== 4 && callout.visible"
      :style="{ left: callout.left + 'px', top: callout.top + 'px' }"
      role="dialog"
      aria-live="polite"
    >
      <div class="callout-step">Step {{ step }}</div>
      <div class="callout-title">{{ callout.title }}</div>
      <div class="callout-body">{{ instructions[step] }}</div>
      <div class="callout-actions">
        <button class="btn ghost small" @click="prevStep" :disabled="step===1">Back</button>
        <button class="btn emph small" v-if="step < 5" @click="nextStep">Next</button>
        <button class="btn emph small" v-else-if="step === 5" @click="nextStep">Next</button>
        <button class="btn emph small" v-else-if="step === 6" @click="nextStep">Next</button>
      </div>
    </div>

<!-- Waiting backdrop + callout (step 4) -->
<div
  v-show="!showIntro && step === 4"
  class="intro-backdrop"
  role="status"
  aria-live="polite"
  aria-busy="true"
>
  <div class="callout waiting" 
     data-track="tut_waiting_view"
     :data-track-props="JSON.stringify({ step })">>
    <div class="callout-step">Step 4</div>
    <div class="callout-title">Wait for the request to process</div>

    <div class="callout-body">
      <div class="wait-row">
        <div class="spinner" aria-hidden="true"></div>
        <div class="wait-text">
          Analyzing
          <span class="dots"><span>.</span><span>.</span><span>.</span></span>
        </div>
      </div>
      <p class="small">We’re analyzing the transaction. This usually takes a few seconds.</p>
    </div>
  </div>
</div>


    <!-- Report modal -->
    <div v-if="reportOpen" class="modal-backdrop" role="dialog" aria-modal="true">
      <div class="modal">
        <h3>Example Report</h3>
        <p><strong>Initial transaction:</strong> 50,000.00 USDT</p>
        <p><strong>Green flags:</strong> 50,000.00 USDT</p>
        <hr />
        <!-- inside the report modal, under <hr /> -->
<p class="small">
  <img src="../assets/greenflag.png" alt="Green flag" style="height:1em;vertical-align:-2px;margin-right:6px;">
  Funds likely went to an exchange.
</p>
        <p class="small">
          “Green flags” indicate we found entities that are potentially subpoenable.
          In this example, funds travel a few hops and land at an exchange (a green flag).
          On the network view, you’d see blue circles (addresses), yellow squares (transactions),
          and flags over blue circles where paths end at an exchange.
        </p>
        <div class="modal-actions">
         <button
  class="btn primary"
  @click="ackReport"
  data-track="tut_report_ack"
>Got it</button>
        </div>
      </div>
    </div>

    <!-- Image modal -->
    <div v-if="imageOpen" class="modal-backdrop" role="dialog" aria-modal="true">
      <div class="modal modal-wide">
        <h3>Example Transaction Graph</h3>
        <div class="img-wrap">
          <!-- Using relative import path pattern you mentioned -->
          <img src="../assets/ExampleTransaction.png" alt="Example transaction diagram" />
        </div>
        <p class="small">
          Illustration: blue circles are addresses, yellow squares denote transactions,
          and the end path hits an likely subpoenable exchange (green flag).
        </p>
        <div class="modal-actions">
        <button
  class="btn primary"
  @click="closeImage"
  data-track="tut_image_close"
>Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "BasicTutorial",
  data() {
    return {
      showIntro: true,
    showOutro: false,   // NEW
      step: 1,
      fakeHash: "",
      hole: { x: 0, y: 0, w: 200, h: 60, r: 10, visible: false },
      callout: { visible: false, left: 0, top: 0, title: "" },
      autoTimer: null,
      reportOpen: false,
      imageOpen: false,
      instructions: {
        1: "Copy the outgoing transaction hash shown in the brackets.",
        2: "Paste the hash into this field. In the real tool, we validate it here.",
        3: "Click Submit to send your request.",
        4: "Wait a moment while the request processes.",
        5: "Your result is ready. Click View Report to read the summary.",
        6: "Great — now click View Image to see the diagram.",
        7: "All done! You’ve completed the tutorial."
      }
    };
  },
  
  watch: {
  step(newVal, oldVal) {
    // Show outro when done (keeps your existing behavior)
    if (newVal === 7 && !this.imageOpen) {
      this.showOutro = true;
    }

    // Entering step 4: always start the wait timer
    if (newVal === 4) {
      this.startWaitTimer();
    }

    // Leaving step 4: always clear the timer
    if (oldVal === 4) {
      clearTimeout(this.autoTimer);
      this.autoTimer = null;
    }
  }
},


  computed: {
    holeStyle() {
      return {
        left: this.hole.x + "px",
        top: this.hole.y + "px",
        width: this.hole.w + "px",
        height: this.hole.h + "px",
        borderRadius: this.hole.r + "px"
      };
    },  overlayActive() {
    return this.showIntro || this.showOutro || this.reportOpen || this.imageOpen;
  }
  },
mounted() {
  this.$el.focus?.();
  window.addEventListener("resize", this.ensureSpotlight, { passive: true });
  window.addEventListener("scroll", this.ensureSpotlight, { passive: true });
  window.addEventListener("keydown", this.handleKey);
},
beforeUnmount() {
  window.removeEventListener("resize", this.ensureSpotlight);
  window.removeEventListener("scroll", this.ensureSpotlight);
  window.removeEventListener("keydown", this.handleKey);
  clearTimeout(this.autoTimer);
},
  methods: {
     startWaitTimer() {
    clearTimeout(this.autoTimer);
    // Keep the backdrop/callout state consistent for step 4
    this.hole.visible = false;
    this.callout.visible = false;

    this.autoTimer = setTimeout(() => {
      this.step = 5;
      this.$nextTick(this.ensureSpotlight);
    }, 1800);
  },

    handleKey(e) {
    // Don’t let shortcuts run under modals/intro/outro
    if (this.overlayActive) {
      // Optional: allow Esc to close report/image only
      if (e.code === "Escape") {
        if (this.reportOpen) { e.preventDefault(); this.ackReport(); }
        if (this.imageOpen)  { e.preventDefault(); this.closeImage(); }
      }
      return;
    }

    // Ignore when focused in inputs, textareas, selects, contenteditable
    const ae = document.activeElement;
    const tag = (ae && ae.tagName) || "";
    const isTyping =
      tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (ae && ae.isContentEditable);
    if (isTyping) return;

    // Ignore with modifier keys
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.code === "Space") {
      e.preventDefault(); // stop page scroll
      if (this.step < 7) this.nextStep();
    } else if (e.code === "Escape") {
      e.preventDefault();
      this.prevStep();
    }
  },

    beginTutorial() {
      this.showIntro = false;
      this.$nextTick(this.ensureSpotlight);
    },
skipTutorial() {
  this.showIntro = false;
  this.step = 7;
  this.hole.visible = false;
  this.callout.visible = false;
  this.showOutro = true; // <- ensure outro appears on skip
},

    nextStep() {
      if (this.step === 3) {
        this.goToWaiting();
        return;
      }
      this.step = Math.min(7, this.step + 1);
      this.$nextTick(this.ensureSpotlight);
    },
    prevStep() {
  clearTimeout(this.autoTimer);
  this.autoTimer = null;
  this.step = Math.max(1, this.step - 1);
  this.$nextTick(this.ensureSpotlight);
},

restart() {
  this.fakeHash = "";
  this.step = 1;
  this.showIntro = true;
  this.showOutro = false;  // reset
  this.hole.visible = false;
  this.callout.visible = false;
  this.reportOpen = false;
  this.imageOpen = false;
  clearTimeout(this.autoTimer);
}
,
    goToWaiting() {
      this.step = 4;
      // Step 4 uses the centered callout; keep backdrop by leaving hole hidden
      this.hole.visible = false;
      this.callout.visible = false;
   
    },
    openReport() {
      this.reportOpen = true;
    },
    ackReport() {
      this.reportOpen = false;
      // Teach them to view the image next
      this.step = 6;
      this.$nextTick(this.ensureSpotlight);
    },
    openImage() {
      this.imageOpen = true;
    },
closeImage() {
  this.imageOpen = false;
  this.step = 7;
  this.hole.visible = false;
  this.callout.visible = false;
  this.showOutro = true; // stays
},

    ensureSpotlight() {
      if (this.showIntro) return;
      if (this.step === 4 || this.step === 7) {
        this.hole.visible = false;
        this.callout.visible = false;
        return;
      }
      const target = this.$el.querySelector('[data-focus="true"]');
      const fallback = this.$refs.txRow;
      const el = target || fallback;
      if (!el) {
        this.hole.visible = false;
        this.callout.visible = false;
        return;
      }
      const rect = el.getBoundingClientRect();
      const pad = 12;
      const x = Math.round(rect.left - pad);
      const y = Math.round(rect.top - pad);
      const w = Math.round(rect.width + pad * 2);
      const h = Math.round(rect.height + pad * 2);

      this.hole = { x, y, w, h, r: 10, visible: true };

      const title = el.getAttribute("data-focus-tag") || "Do this";
      this.callout.title = title;

      const calloutWidth = 320;
      const gap = 12;
      const vw = document.documentElement.clientWidth;
      let left = x + w + gap;
      let top = y;
      if (left + calloutWidth + 16 > vw) {
        left = x;
        top = y + h + gap;
      }
      this.callout.left = left;
      this.callout.top = top;
      this.callout.visible = true;
    }
  }
};
</script>

<style scoped>
/* Layout basics */
.tutorial-root { position: relative; padding: 24px; outline: none; }
.tutorial-header { margin-bottom: 16px; }
.tutorial-header h1 { margin: 0 0 6px 0; font-size: 22px; }
.tutorial-header .sub { margin: 0; color: #555; }

/* Intro modal */
.intro-backdrop {
  position: fixed; inset: 0; background: rgba(10,12,17,0.65);
  display: grid; place-items: center; z-index: 100;
}
.intro-modal {
  width: min(680px, 92vw); background: #0f1320; color: #fff; border-radius: 16px;
  padding: 20px; border: 1px solid rgba(255,255,255,0.08);
  box-shadow: 0 20px 60px rgba(0,0,0,0.45);
}
.intro-modal h2 { margin: 0 0 8px 0; }
.intro-modal p { margin: 0 0 10px 0; color: #d6d9e0; }
.intro-points { margin: 0 0 16px 20px; color: #d6d9e0; }
.intro-actions { display: flex; gap: 10px; justify-content: flex-end; }

/* Grid & cards */
.grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media (min-width: 900px) { .grid { grid-template-columns: 1fr 1fr 1fr; align-items: start; } }
.card {
  background: #fff; border: 1px solid #e6e6e9; border-radius: 12px; padding: 16px;
  box-shadow: 0 1px 2px rgba(15, 15, 20, 0.06);
}
.card-title { margin: 0 0 12px 0; font-size: 18px; }

/* Mock table */
.mock-table { width: 100%; border-collapse: collapse; font-size: 14px; }
.mock-table th, .mock-table td { border: 1px solid #ddd; padding: 8px 10px; text-align: left; vertical-align: middle; }
.mock-table thead th { background: #fafafa; font-weight: 600; }
.mock-table .highlighted { background: #fffae6; }

/* Braces around focus */
.brace-focus { position: relative; font-weight: 600; }
.brace-focus span { position: relative; display: inline-block; padding: 2px 6px; }
.brace-focus span::before, .brace-focus span::after {
  position: absolute; top: 50%; transform: translateY(-50%);
  font-weight: 800; font-size: 18px; color: #333; opacity: 0.9;
}
.brace-focus span::before { content: "【"; left: -14px; }
.brace-focus span::after { content: "】"; right: -14px; }

/* Fields & buttons */
.field { display: grid; gap: 6px; margin-bottom: 12px; }
.field label { font-size: 13px; color: #444; }
.field input {
  height: 36px; padding: 6px 10px; border: 1px solid #cfd3d8; border-radius: 8px; font-size: 14px; outline: none;
}
.field input:focus { border-color: #6aa0ff; box-shadow: 0 0 0 3px rgba(106, 160, 255, 0.2); }
.hint { color: #666; margin: 0; font-size: 12px; }

.actions { display: flex; gap: 8px; }

/* Buttons */
.btn { border: 1px solid #d0d5dd; background: #f6f7f9; color: #111; border-radius: 10px; padding: 8px 12px; font: inherit; cursor: pointer; }
.btn:hover { background: #eef1f6; }
.btn.primary { background: #1e66ff; color: #fff; border-color: #1e66ff; }
.btn.primary:hover { filter: brightness(0.95); }
.btn.ghost { background: transparent; color: #111; }
.btn.small { padding: 6px 10px; }

/* High-contrast buttons inside dark callout */
.callout .btn { background: #ffffff; color: #101319; border-color: #ffffff; }
.callout .btn:hover { filter: brightness(0.97); }
.callout .btn.ghost { background: transparent; color: #ffffff; border-color: rgba(255,255,255,0.6); }
.callout .btn.ghost:hover { border-color: #ffffff; }
.callout .btn.emph { background: #1e66ff; color: #fff; border-color: #1e66ff; }

/* Pills */
.pill { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 12px; border: 1px solid #e0e3ea; background: #f6f8fb; margin-right: 6px; }
.pill--ok { background: #e8f7ee; border-color: #bfe7cd; color: #237a3b; }

/* Placeholder shimmer */
.placeholder .skeleton {
  height: 14px; border-radius: 6px;
  background: linear-gradient(90deg, #f2f4f7, #e9edf3, #f2f4f7);
  background-size: 200% 100%; animation: shimmer 1.3s infinite; margin-bottom: 8px;
}
.placeholder .skeleton.wide { width: 80%; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.results .result-row { display: flex; gap: 8px; align-items: center; margin-bottom: 10px; }
.result-actions { display: flex; gap: 8px; }

/* Footer controls */
.controls {
  position: sticky; bottom: 0; background: rgba(255,255,255,0.97);
  border-top: 1px solid #eceef2; padding: 10px 16px; margin-top: 16px;
  display: flex; align-items: center; justify-content: space-between;
}
.step-chip { font-size: 12px; padding: 4px 8px; border-radius: 999px; border: 1px solid #e0e3ea; background: #f6f8fb; }
.controls .right { display: flex; gap: 8px; }
.dots span { animation: dots 1s infinite steps(1, end); display: inline-block; width: 6px; text-align: center; }
.dots span:nth-child(2) { animation-delay: .2s; }
.dots span:nth-child(3) { animation-delay: .4s; }
@keyframes dots { 0%, 80% { opacity: 0; } 100% { opacity: 1; } }

/* Spotlight hole (fixed to viewport) */
.spotlight-hole {
  position: fixed; z-index: 60; pointer-events: none;
  background: transparent;
  box-shadow: 0 0 0 9999px rgba(10, 12, 17, 0.55);
  transition: left .15s ease, top .15s ease, width .15s ease, height .15s ease;
}

/* Callouts */
.callout.fixed {
  position: fixed; z-index: 70; width: 320px;
  background: #101319; color: #fff; border-radius: 14px; padding: 12px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.05) inset;
  border: 1px solid rgba(255,255,255,0.08);
}
.callout.waiting {
  position: fixed; z-index: 70; width: 360px;
  left: 50%; top: 20vh; transform: translateX(-50%);
  background: #101319; color: #fff; border-radius: 14px; padding: 14px;
  text-align: left;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35), 0 1px 0 rgba(255,255,255,0.05) inset;
  border: 1px solid rgba(255,255,255,0.08);
}
.callout-step { font-size: 11px; letter-spacing: 0.08em; text-transform: uppercase; opacity: 0.7; margin-bottom: 6px; }
.callout-title { font-weight: 700; margin-bottom: 6px; }
.callout-body { font-size: 13px; line-height: 1.35; opacity: 0.95; }

/* Modals */
.modal-backdrop {
  position: fixed; inset: 0; background: rgba(10,12,17,0.65);
  display: grid; place-items: center; z-index: 120;
}
.modal {
  width: min(720px, 92vw); background: #fff; color: #111; border-radius: 16px;
  padding: 18px; border: 1px solid #e7e9ee; box-shadow: 0 20px 60px rgba(0,0,0,0.35);
}
.modal-wide { width: min(980px, 94vw); }
.modal h3 { margin: 0 0 8px 0; }
.modal p { margin: 6px 0; }
.modal .small { font-size: 13px; color: #444; }
.modal-actions { margin-top: 12px; display: flex; justify-content: flex-end; gap: 8px; }
.img-wrap { margin: 10px 0; overflow: auto; }
.img-wrap img { display: block; max-width: 100%; height: auto; border-radius: 8px; }

/* Accessibility */
.tutorial-root:focus-visible { outline: 2px solid #1e66ff; outline-offset: 4px; }

/* Fix ghost contrast in intro modal */
.intro-modal .btn.ghost {
  color: #fff;
  border-color: rgba(255,255,255,0.6);
}
.intro-modal .btn.ghost:hover {
  border-color: #fff;
  background: rgba(255,255,255,0.1);
}/* Raise above any other overlays */
.intro-backdrop {
  z-index: 140;
}
/* Step 4: spinner row */
.wait-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 6px 0 8px 0;
}

/* Circular spinner */
.spinner {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(255,255,255,0.25);
  border-top-color: #ffffff;
  animation: spin 0.9s linear infinite;
}

/* Spin animation */
@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Respect reduced motion */
@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation: none;
    border-top-color: rgba(255,255,255,0.85);
  }
  .dots span {
    animation: none;
    opacity: 1;
  }
}

/* Optional: tighten .small color on dark panel if desired */
.callout.waiting .small {
  color: #cfd3db;
}
.callout-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;   /* NEW spacing */
  margin-top: 8px; /* optional breathing room */
}
.readmore {
  margin-top: 14px;
  font-size: 14px;
  color: #d6d9e0;
  text-align: right;
}
.readmore a {
  color: #6aa0ff;
  text-decoration: underline;
}
.readmore a:hover {
  color: #fff;
}

</style>
